/**
 * @file src/core/bridge.js
 * postMessage contract v1.0 — 5 message. ĐÂY LÀ ĐOẠN CODE SẼ BỊ ĐÓNG BĂNG
 * trong mọi artifact đã deploy. Mỗi dòng phải sống được 5 năm không sửa.
 *
 *   lesson → FE :  ready · error · progress
 *   FE → lesson :  setLanguage · setActive
 *
 * Quy tắc tiến hoá: CHỈ THÊM, không đổi, không xoá. Xem AGENT.md mục 6.
 */

export const NS = 'ol3d';
export const PROTOCOL = 1;

/**
 * Allowlist TUYỆT ĐỐI (không dùng regex mở như /\.onluyen\.edu\.vn$/ vì
 * subdomain-takeover là rủi ro thật với tổ chức có nhiều subdomain).
 * tools/build-lesson.mjs strip entry localhost khi build production.
 */
export const ALLOWED_PARENT_ORIGINS = Object.freeze([
  'https://hoc.onluyen.edu.vn',
  'https://onluyen.edu.vn',
  'https://staging-hoc.onluyen.edu.vn',
  'http://localhost:5173',
  'http://localhost:5174',
]);

const PROGRESS_DEBOUNCE_MS = 2000;
const PROGRESS_MIN_INTERVAL_MS = 1000;

/**
 * @typedef {Object} BridgeConfig
 * @property {string} lessonId
 * @property {number} buildVersion
 * @property {string} coreVersion
 * @property {string} engineApi
 * @property {string} contentVersion
 * @property {string[]} capabilities
 * @property {string[]} languages
 * @property {(lang: string) => boolean} onSetLanguage  false ⇒ phát error LANG_UNSUPPORTED
 * @property {(active: boolean, reason: string) => void} onSetActive
 */

/**
 * @typedef {Object} Bridge
 * @property {string | null} parentOrigin
 * @property {boolean} enabled
 * @property {(extra: Record<string, any>) => void} emitReady
 * @property {(code: string, o: {fatal?: boolean, retryable?: boolean, message: string, detail?: string, assetKey?: string, httpStatus?: number}) => void} emitError
 * @property {(payload: Record<string, any>, opts?: {immediate?: boolean}) => void} emitProgress
 * @property {() => void} dispose
 * @property {() => Array<{dir: 'in'|'out', type: string, ts: number, payload: any}>} log  Debug panel đọc
 */

/**
 * @param {BridgeConfig} cfg
 * @returns {Bridge}
 */
export function createBridge(cfg) {
  const q = new URLSearchParams(location.search);
  const requested = q.get('parentOrigin');
  const embedded = window.parent !== window;

  const parentOrigin =
    embedded && requested && ALLOWED_PARENT_ORIGINS.includes(requested) ? requested : null;

  if (embedded && !parentOrigin) {
    // Bài VẪN CHẠY (yêu cầu "tự chạy được khi mở URL trực tiếp"), chỉ không phát message.
    console.warn('[bridge] parentOrigin không hợp lệ:', requested, '→ tắt bridge');
  }

  let outSeq = 0;
  let readySent = false;
  let lastProgressAt = 0;
  let progressTimer = 0;
  /** @type {Record<string, any> | null} */
  let pendingProgress = null;
  let lastState = '';
  let lastCompletion = -1;

  /** @type {Array<{dir: 'in'|'out', type: string, ts: number, payload: any}>} */
  const msgLog = [];
  /** @param {'in'|'out'} dir @param {string} type @param {any} payload */
  const pushLog = (dir, type, payload) => {
    msgLog.push({ dir, type, ts: Math.round(performance.now()), payload });
    if (msgLog.length > 100) msgLog.shift();
  };

  /** @param {string} type @param {any} payload */
  function post(type, payload) {
    pushLog('out', type, payload);
    if (!parentOrigin) return;
    window.parent.postMessage(
      { ns: NS, v: PROTOCOL, type, lessonId: cfg.lessonId, seq: ++outSeq, ts: Date.now(), payload },
      parentOrigin, // KHÔNG BAO GIỜ '*' — payload chứa dữ liệu học sinh
    );
  }

  /** @param {Record<string, any>} extra */
  function emitReady(extra) {
    if (readySent) return;
    readySent = true;
    post('ready', {
      protocol: PROTOCOL,
      lessonId: cfg.lessonId,
      buildVersion: cfg.buildVersion,
      coreVersion: cfg.coreVersion,
      engineApi: cfg.engineApi,
      contentVersion: cfg.contentVersion,
      capabilities: cfg.capabilities,
      languages: cfg.languages,
      ...extra,
    });
  }

  /** @param {string} code @param {{fatal?: boolean, retryable?: boolean, message: string, detail?: string, assetKey?: string, httpStatus?: number}} o */
  function emitError(code, o) {
    post('error', {
      code,
      fatal: o.fatal ?? false,
      retryable: o.retryable ?? false,
      message: o.message,
      detail: o.detail,
      assetKey: o.assetKey,
      httpStatus: o.httpStatus,
    });
  }

  /** @param {Record<string, any>} p @param {{immediate?: boolean}} [o] */
  function emitProgress(p, o = {}) {
    const immediate = o.immediate ?? false;
    const stateChanged = p.state !== lastState;
    const jumped = Number(p.completion ?? 0) - lastCompletion >= 0.05;
    const force = immediate || stateChanged || jumped;
    pendingProgress = p;

    const flush = () => {
      if (!pendingProgress) return;
      const now = Date.now();
      if (!immediate && now - lastProgressAt < PROGRESS_MIN_INTERVAL_MS) return;
      lastProgressAt = now;
      lastState = String(pendingProgress.state ?? '');
      lastCompletion = Number(pendingProgress.completion ?? 0);
      post('progress', { ...pendingProgress, emittedAt: new Date(now).toISOString() });
      pendingProgress = null;
    };

    if (progressTimer) clearTimeout(progressTimer);
    if (force) flush();
    else progressTimer = window.setTimeout(flush, PROGRESS_DEBOUNCE_MS);
  }

  /** @param {MessageEvent} ev */
  function onMessage(ev) {
    if (!parentOrigin) return;
    if (ev.source !== window.parent) return;
    if (ev.origin !== parentOrigin) return;
    const d = ev.data;
    if (!d || typeof d !== 'object' || d.ns !== NS) return;
    if (d.v !== PROTOCOL) return; // protocol 2.0 tương lai: THÊM nhánh, không sửa nhánh này
    if (d.lessonId !== cfg.lessonId) return;

    pushLog('in', String(d.type), d.payload);

    switch (d.type) {
      case 'setLanguage': {
        const lang = d.payload?.lang;
        if (typeof lang !== 'string' || !cfg.languages.includes(lang) || !cfg.onSetLanguage(lang)) {
          emitError('LANG_UNSUPPORTED', {
            fatal: false,
            retryable: false,
            message: 'Lesson does not support this language.',
            detail: `requested=${String(lang)} available=${cfg.languages.join(',')}`,
          });
        }
        return;
      }
      case 'setActive': {
        const active = d.payload?.active;
        if (typeof active !== 'boolean') return;
        cfg.onSetActive(active, String(d.payload?.reason ?? 'user'));
        return;
      }
      default:
        // BẮT BUỘC có default. Đây là dòng cho phép thêm lệnh mới trong 5 năm tới
        // mà hàng trăm bài đã đóng băng vẫn không vỡ.
        return;
    }
  }

  window.addEventListener('message', onMessage);

  const onPageHide = () => {
    if (pendingProgress) emitProgress(pendingProgress, { immediate: true });
  };
  window.addEventListener('pagehide', onPageHide);

  return {
    parentOrigin,
    enabled: parentOrigin !== null,
    emitReady,
    emitError,
    emitProgress,
    log: () => msgLog,
    dispose() {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('pagehide', onPageHide);
      if (progressTimer) clearTimeout(progressTimer);
    },
  };
}
